import { useMotionValueEvent, useScroll } from "motion/react";
import { type RefObject, useEffect, useRef, useState } from "react";

/**
 * Configuration options for the `useFloatingToolbarScrollBehavior` hook.
 */
export interface UseFloatingToolbarScrollBehaviorOptions {
	/** Direction toolbar exits: 'top' | 'bottom' | 'start' | 'end'. Defaults to 'bottom'. */
	exitDirection?: "top" | "bottom" | "start" | "end";
	/** Scroll distance threshold to trigger collapse (in px). Defaults to 10. */
	collapseThreshold?: number;
	/** Scroll distance threshold to trigger expand (in px). Defaults to 10. */
	expandThreshold?: number;
	/** Optional ref to a scrollable container. If not provided, it listens to window scroll. */
	scrollContainerRef?: RefObject<HTMLElement | null>;
}

/**
 * The resulting behavior object returned by `useFloatingToolbarScrollBehavior`.
 */
export interface FloatingToolbarScrollBehavior {
	/** Current offset. 0 is fully visible, -1 is fully hidden. */
	offset: number;
	/** Whether the toolbar is currently expanded. */
	isExpanded: boolean;
	/** Optional scroll handler to bind to a React scrollable container (legacy fallback). */
	onScroll?: (event: React.UIEvent<HTMLElement>) => void;
	/** Manually update the expanded state of the toolbar. */
	setExpanded: (expanded: boolean) => void;
	/** The configured exit direction. */
	exitDirection: "top" | "bottom" | "start" | "end";
}

/**
 * A hook that provides scroll behavior for floating toolbars, allowing them to
 * collapse (exit) when scrolling down and expand (enter) when scrolling up.
 * Leverages Framer Motion's useScroll internally for optimized performance.
 *
 * @param options Configuration options for scroll behavior.
 * @returns An object containing the current scroll state and handlers.
 *
 * @example
 * ```tsx
 * const scrollBehavior = useFloatingToolbarScrollBehavior({
 *   exitDirection: 'bottom',
 *   collapseThreshold: 20
 * });
 *
 * <HorizontalFloatingToolbar scrollBehavior={scrollBehavior}>
 *   <IconButton icon="bold" />
 * </HorizontalFloatingToolbar>
 * ```
 */
export function useFloatingToolbarScrollBehavior(
	options: UseFloatingToolbarScrollBehaviorOptions = {},
): FloatingToolbarScrollBehavior {
	const {
		exitDirection = "bottom",
		collapseThreshold = 10,
		expandThreshold = 10,
		scrollContainerRef,
	} = options;

	const [isExpanded, setIsExpanded] = useState(true);
	const [offset, setOffset] = useState(0); // 0 to -1

	const { scrollY } = useScroll({
		container: scrollContainerRef,
	});

	const lastScrollY = useRef(0);
	const accumulatedScroll = useRef(0);

	useMotionValueEvent(scrollY, "change", (currentScrollY) => {
		const deltaY = currentScrollY - lastScrollY.current;

		// Ignore bouncing (negative scroll or past max scroll on macOS)
		if (currentScrollY < 0) return;

		lastScrollY.current = currentScrollY;

		// Only accumulate if we are scrolling in the same direction
		if (Math.sign(deltaY) !== Math.sign(accumulatedScroll.current)) {
			accumulatedScroll.current = deltaY;
		} else {
			accumulatedScroll.current += deltaY;
		}

		if (deltaY > 0 && isExpanded) {
			// Scrolling down -> collapse
			if (accumulatedScroll.current > collapseThreshold) {
				setIsExpanded(false);
				setOffset(-1);
			}
		} else if (deltaY < 0 && !isExpanded) {
			// Scrolling up -> expand
			if (Math.abs(accumulatedScroll.current) > expandThreshold) {
				setIsExpanded(true);
				setOffset(0);
			}
		}
	});

	// Accessibility: Always force expanded if forced-colors mode or accessibility preferences
	useEffect(() => {
		if (typeof window !== "undefined") {
			const matchMedia = window.matchMedia("(forced-colors: active)");
			if (matchMedia.matches) {
				setIsExpanded(true);
				setOffset(0);
			}

			const listener = (e: MediaQueryListEvent) => {
				if (e.matches) {
					setIsExpanded(true);
					setOffset(0);
				}
			};

			matchMedia.addEventListener("change", listener);
			return () => matchMedia.removeEventListener("change", listener);
		}
	}, []);

	const setExpanded = (expanded: boolean) => {
		setIsExpanded(expanded);
		setOffset(expanded ? 0 : -1);
	};

	// Dummy onScroll for legacy compatibility if users still attach it
	const onScroll = () => {};

	return {
		offset,
		isExpanded,
		onScroll,
		setExpanded,
		exitDirection,
	};
}
